import { ElementType, ReactNode } from 'react';
import { GestureResponderEvent, Pressable, View } from 'react-native';
import { cn } from '../../lib/utils';
import { Loader } from '../Loader/Loader';
import { Typography } from '../Typography/Typography';

export type ButtonSize = 'large' | 'medium' | 'small';
export type ButtonColor =
  | 'primary'
  | 'white'
  | 'gray'
  | 'secondary'
  | 'transparent';

const backgroundColors = {
  primary:
    'bg-primary hover:bg-primary-dark active:bg-primary-dark active:opacity-20',
  white:
    'bg-white hover:bg-neutral-500 active:bg-neutral-500 active:opacity-20',
  // For keyboard overlay, color is not in style guide
  gray: 'bg-[#646464] hover:bg-primary active:bg-primary',
  secondary:
    'bg-transparent hover:bg-neutral-500 active:bg-neutral-500 active:opacity-20',
  transparent: 'bg-transparent',
  disabled: 'bg-[#383C5F]',
} as const;

const paddings = {
  large: 'py-3 px-6',
  medium: 'py-2.5 px-4',
  small: 'py-1.5 px-3',
} as const;

export function Button({
  children,
  text,
  size = 'large',
  color = 'primary',
  icon: Icon,
  loading,
  onPress,
  disabled,
  fullWidth = true,
  className,
}: {
  children?: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  text?: string;
  size?: ButtonSize;
  color?: ButtonColor;
  icon?: ElementType;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}) {
  const backgroundColor =
    backgroundColors[disabled || loading ? 'disabled' : color];
  const padding = paddings[size];
  const textSize = size == 'large' ? 'normal' : 'small';
  const textStyle =
    disabled || loading
      ? 'text-[#A4A7C1]'
      : color == 'white'
        ? 'text-black group-hover:text-white group-active:text-white'
        : 'text-white';
  const iconStyle =
    color == 'white'
      ? 'fill-black group-hover:fill-white group-active:fill-white'
      : 'fill-white';
  const border =
    color == 'secondary' && !(disabled || loading)
      ? 'border border-white hover:border-transparent'
      : '';

  return (
    <Pressable
      disabled={disabled || loading}
      onPress={ev => onPress?.(ev)}
      role="button"
      className={cn(
        'flex group text-center flex-row justify-center items-center rounded-[100px] self-start',
        fullWidth && 'w-full',
        backgroundColor,
        border,
        padding,
        className,
      )}>
      {children || (
        <>
          {Icon && !loading && (
            <View className="mr-2 h-6 w-6 flex items-center justify-center">
              <Icon className={iconStyle} />
            </View>
          )}
          {loading && (
            <View className="mr-2 -my-1">
              <Loader color="#A4A7C1" />
            </View>
          )}
          <Typography
            className={cn('text-center', textStyle)}
            variant="callToAction"
            size={textSize}>
            {text}
          </Typography>
        </>
      )}
    </Pressable>
  );
}
